// services/youtubeService.js
//
// YouTube on the web: sign in with Google, list your playlists, make a new
// one, put videos in it.
//
// ── Why this is not the app's service ──────────────────────────────────────
//
// The app's src/features/music/api/youtubeService.js does the same work, but
// its sign-in cannot be reused. It uses expo-auth-session with the iOS and
// Android OAuth clients and a `com.app.invits:/oauth2redirect` custom-scheme
// redirect — a scheme a browser has no idea what to do with, against client
// ids Google will refuse from a web origin. A browser needs a Web client, and
// a Web client is a different credential in the same Google Cloud project.
//
// ── Which flow ─────────────────────────────────────────────────────────────
//
// Google Identity Services' token flow, not the authorization-code flow the
// app uses. Code exchange needs a client secret, and there is no such thing as
// a secret in a JavaScript bundle. GIS hands a browser an access token
// directly, in a popup, with no secret anywhere — it is what Google documents
// for single-page apps.
//
// The consequence is that the token is short-lived (about an hour) and there
// is no refresh token. That is fine for what this is used for: a host signs
// in, picks or creates a playlist, and is done. Nothing here needs to act on
// their account later.
//
// ── The token is never stored ──────────────────────────────────────────────
//
// It stays in memory for the life of the page. An OAuth token in
// localStorage is readable by any script that manages to run on the origin,
// and this one can edit the person's YouTube account. Signing in again costs
// one click.
//
// ── Setup ──────────────────────────────────────────────────────────────────
//
// REACT_APP_YOUTUBE_WEB_CLIENT_ID must hold a **Web application** OAuth client
// from the same Google Cloud project as the app's (676512905826), with this
// site's origin listed under "Authorised JavaScript origins". Without it,
// isConfigured() is false and the UI offers the paste-a-link path only —
// which is the correct behaviour, not a degraded one: a sign-in button that
// cannot sign in is worse than no button.

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const API = 'https://www.googleapis.com/youtube/v3';

/** Enough to read and edit the person's own playlists. Nothing more. */
const SCOPE = 'https://www.googleapis.com/auth/youtube';

const CLIENT_ID = process.env.REACT_APP_YOUTUBE_WEB_CLIENT_ID || '';

/** In memory only, deliberately. See the note above. */
let accessToken = null;
let expiresAt = 0;

export function isConfigured() {
  return !!CLIENT_ID;
}

export function isSignedIn() {
  return !!accessToken && Date.now() < expiresAt;
}

export function signOut() {
  accessToken = null;
  expiresAt = 0;
}

/**
 * Loads Google's script once, and resolves when `google.accounts` is ready.
 *
 * Exported, and called on mount by anything that might offer sign-in, because
 * of what happens if it is not — see signIn below.
 */
let gisPromise = null;
export function loadGis() {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (gisPromise) return gisPromise;

  gisPromise = new Promise((resolve, reject) => {
    const fail = () => {
      // Cleared on failure so a later attempt can retry rather than being
      // handed back the same rejected promise for the life of the page —
      // which would make a momentary network blip permanent.
      gisPromise = null;
      reject(new Error('Could not load Google sign-in.'));
    };

    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      // A script that has already finished will never fire `load` again, so
      // listening alone would hang for ever. Google sets a flag on the tag
      // when it is done; failing that, poll briefly for the global.
      if (window.google?.accounts?.oauth2) { resolve(); return; }
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', fail);
      return;
    }

    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = fail;
    document.head.appendChild(script);
  });

  return gisPromise;
}

/** Is the script in place, so requestAccessToken can be called synchronously? */
export function isGisReady() {
  return !!window.google?.accounts?.oauth2;
}

/**
 * Opens Google's consent popup and keeps the token it returns.
 *
 * ── Why this is not `async` any more ───────────────────────────────────────
 *
 * It used to `await loadGis()` before calling requestAccessToken. That reads
 * fine and breaks in Safari: a browser only lets a popup open while it is
 * still handling a user gesture, and an `await` ends that window. The click
 * would be handled, the script fetched, the popup requested a tick later —
 * and blocked. The symptom is the worst kind: nothing happens, no error.
 *
 * So the script is loaded ahead of time (the picker calls loadGis on mount)
 * and this function is synchronous from the click to the popup. If the script
 * is somehow not ready yet, it says so rather than opening a popup that will
 * be swallowed.
 *
 * @returns {Promise<string>} the access token
 */
export function signIn() {
  if (!CLIENT_ID) {
    return Promise.reject(new Error(
      'YouTube sign-in is not configured on this site (REACT_APP_YOUTUBE_WEB_CLIENT_ID).',
    ));
  }

  if (!isGisReady()) {
    // Start it, so the next click works.
    loadGis().catch(() => {});
    return Promise.reject(new Error('Google sign-in is still loading — try again in a moment.'));
  }

  return new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPE,
      callback: (response) => {
        if (response.error) {
          reject(new Error(response.error_description || response.error));
          return;
        }
        accessToken = response.access_token;
        // A minute short of what Google says, so a request never goes out
        // with a token that expires while it is in flight.
        expiresAt = Date.now() + (Number(response.expires_in || 3600) - 60) * 1000;
        resolve(accessToken);
      },
      error_callback: (err) => {
        // The person closing the popup is not an error worth shouting about.
        reject(
          new Error(
            err?.type === 'popup_closed'
              ? 'Sign-in was cancelled.'
              : 'Could not sign in to YouTube.',
          ),
        );
      },
    });

    client.requestAccessToken();
  });
}

async function request(path, { method = 'GET', params = {}, body = null } = {}) {
  if (!isSignedIn()) throw new Error('Not signed in to YouTube.');

  const url = new URL(API + path);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined) url.searchParams.set(k, String(v));
  });

  const response = await fetch(url.toString(), {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401) {
    // Expired or revoked. Clearing it means the UI offers sign-in again
    // rather than repeating a request that cannot succeed.
    signOut();
    throw new Error('Your YouTube sign-in expired. Sign in again.');
  }

  if (!response.ok) {
    let message = `YouTube said no (${response.status}).`;
    try {
      const err = await response.json();
      message = err?.error?.message || message;
    } catch {
      // Keep the status-code message.
    }
    throw new Error(message);
  }

  return response.status === 204 ? null : response.json();
}

/** The playlists on the signed-in account, newest first. */
export async function getMyPlaylists(maxResults = 50) {
  const data = await request('/playlists', {
    params: { part: 'snippet,status,contentDetails', mine: 'true', maxResults },
  });

  return (data?.items ?? []).map((item) => ({
    id: item.id,
    title: item.snippet?.title ?? 'Untitled',
    description: item.snippet?.description ?? '',
    thumbnail:
      item.snippet?.thumbnails?.medium?.url ?? item.snippet?.thumbnails?.default?.url ?? null,
    itemCount: item.contentDetails?.itemCount ?? 0,
    privacyStatus: item.status?.privacyStatus ?? 'private',
  }));
}

/**
 * Makes a playlist.
 *
 * Public by default, and that is not a detail: a private playlist plays for
 * its owner and for nobody else, so a host who made one here would hear music
 * and every guest would get an error. The app warns about exactly this when a
 * host picks a non-public playlist; creating one, the right thing is to
 * default to the setting that works.
 */
export async function createPlaylist(title, description = '', privacyStatus = 'public') {
  const data = await request('/playlists', {
    method: 'POST',
    params: { part: 'snippet,status' },
    body: {
      snippet: { title, description },
      status: { privacyStatus },
    },
  });

  return {
    id: data.id,
    title: data.snippet?.title ?? title,
    description: data.snippet?.description ?? '',
    thumbnail: null,
    itemCount: 0,
    privacyStatus: data.status?.privacyStatus ?? privacyStatus,
  };
}

export async function addVideoToPlaylist(playlistId, videoId) {
  return request('/playlistItems', {
    method: 'POST',
    params: { part: 'snippet' },
    body: {
      snippet: {
        playlistId,
        resourceId: { kind: 'youtube#video', videoId },
      },
    },
  });
}

/** What is in a playlist, for showing the host what they picked. */
export async function getPlaylistItems(playlistId, maxResults = 25) {
  const data = await request('/playlistItems', {
    params: { part: 'snippet', playlistId, maxResults },
  });

  return (data?.items ?? []).map((item) => ({
    id: item.id,
    title: item.snippet?.title ?? 'Untitled',
    videoId: item.snippet?.resourceId?.videoId ?? null,
    thumbnail:
      item.snippet?.thumbnails?.default?.url ?? item.snippet?.thumbnails?.medium?.url ?? null,
  }));
}

/** Turn a public playlist private-to-public, for fixing the common mistake. */
export async function makePlaylistPublic(playlistId, title) {
  return request('/playlists', {
    method: 'PUT',
    params: { part: 'snippet,status' },
    // The API replaces the resource rather than patching it, so the title has
    // to go back with it or it would be blanked.
    body: {
      id: playlistId,
      snippet: { title },
      status: { privacyStatus: 'public' },
    },
  });
}
